import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import appointmentService from "../../services/appointmentService";

export const fetchAppointments = createAsyncThunk(
  "appointments/fetchAppointments",
  async (doctorId) => {
    return await appointmentService.getAppointments(doctorId);
  },
);

export const addAppointment = createAsyncThunk(
  "appointments/addAppointment",
  async (appointmentData, { rejectWithValue }) => {
    try {
      return await appointmentService.createAppointment(appointmentData);
    } catch (error) {
      return rejectWithValue(error.message || "Failed to create appointment");
    }
  },
);

export const createPatientWithAppointment = createAsyncThunk(
  "appointments/createPatientWithAppointment",
  async (data, { rejectWithValue }) => {
    try {
      return await appointmentService.createPatientWithAppointment(data);
    } catch (error) {
      return rejectWithValue(error.message || "Failed to create patient and appointment");
    }
  },
);

export const updateAppointmentData = createAsyncThunk(
  "appointments/updateAppointmentData",
  async ({ id, appointmentData }) => {
    return await appointmentService.updateAppointment(id, appointmentData);
  },
);

export const removeAppointment = createAsyncThunk(
  "appointments/removeAppointment",
  async (id) => {
    await appointmentService.deleteAppointment(id);
    return id;
  },
);

export const searchAppointments = createAsyncThunk(
  "appointments/searchAppointments",
  async ({ query, doctorId }) => {
    return await appointmentService.searchAppointments(query, doctorId);
  },
);

const appointmentSlice = createSlice({
  name: "appointments",
  initialState: {
    appointments: [],
    loading: false,
    error: null,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAppointments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAppointments.fulfilled, (state, action) => {
        state.loading = false;
        state.appointments = action.payload;
      })
      .addCase(fetchAppointments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(searchAppointments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchAppointments.fulfilled, (state, action) => {
        state.loading = false;
        state.appointments = action.payload;
      })
      .addCase(searchAppointments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(addAppointment.fulfilled, (state, action) => {
        state.appointments.push(action.payload);
        state.error = null;
      })
      .addCase(addAppointment.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(addAppointment.pending, (state) => {
        state.error = null;
      })
      .addCase(createPatientWithAppointment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPatientWithAppointment.fulfilled, (state, action) => {
        state.loading = false;
        // Add the appointment to the list
        if (action.payload.appointment) {
          state.appointments.push(action.payload.appointment);
        }
        state.error = null;
      })
      .addCase(createPatientWithAppointment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateAppointmentData.fulfilled, (state, action) => {
        const updatedAppointment = action.payload;
        const index = state.appointments.findIndex(
          (a) => a.appointment_id === updatedAppointment.appointment_id
        );
        if (index !== -1) {
          state.appointments[index] = updatedAppointment;
        }
      })
      .addCase(removeAppointment.fulfilled, (state, action) => {
        state.appointments = state.appointments.filter(
          (a) => a.appointment_id !== action.payload,
        );
      });
  },
});

export default appointmentSlice.reducer;
